/**
 * Flyer Types API
 */
import { apiClient } from "./client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { showError, showSuccess } from "../shared/toast";

export interface FlyerType {
    id: string;
    owningTeamId: string;
    name: string;
    summary?: string;
    imageUrl?: string;
    isArchived?: boolean;
    createdAt?: string;
    createdBy?: string;
    updatedAt?: string;
    updatedBy?: string;
}

export interface FlyerTypeCreateRequest {
    name: string;
    summary?: string;
    imageUrl?: string;
}

export interface FlyerTypeUpdateRequest {
    name?: string;
    summary?: string;
    imageUrl?: string;
}

const compareFlyerNames = (a: string, b: string) =>
    a.localeCompare(b, "sv-SE", { sensitivity: "base" });

/**
 * Fetches all flyer types
 */
export const fetchFlyerTypes = async (): Promise<FlyerType[]> => {
    const response = await apiClient.get<FlyerType[]>("/FlyerTypes");
    return [...response.data].sort((a, b) => compareFlyerNames(a.name, b.name));
};

/**
 * Fetches a single flyer type by ID
 */
export const fetchFlyerType = async (id: string): Promise<FlyerType> => {
    const response = await apiClient.get<FlyerType>(`/FlyerTypes/${id}`);
    return response.data;
};

/**
 * Creates a new flyer type
 */
export const createFlyerType = async (
    request: FlyerTypeCreateRequest
): Promise<FlyerType> => {
    const response = await apiClient.post<FlyerType>("/FlyerTypes", request);
    return response.data;
};

/**
 * Updates a flyer type
 */
export const updateFlyerType = async (
    id: string,
    request: FlyerTypeUpdateRequest
): Promise<FlyerType> => {
    const response = await apiClient.put<FlyerType>(`/FlyerTypes/${id}`, request, {
        headers: {
            "Content-Type": "application/json"
        }
    });
    return response.data;
};

/**
 * Deletes a flyer type
 */
export const deleteFlyerType = async (id: string): Promise<void> => {
    await apiClient.delete(`/FlyerTypes/${id}`);
};

/**
 * React Query hooks
 */
export const useFlyerTypes = () => {
    return useQuery({
        queryKey: ["flyer-types"],
        queryFn: fetchFlyerTypes,
        staleTime: 5 * 60 * 1000
    });
};

export const useFlyerType = (id: string | null) => {
    return useQuery({
        queryKey: ["flyer-types", id],
        queryFn: () => fetchFlyerType(id!),
        enabled: !!id,
        staleTime: 5 * 60 * 1000
    });
};

export const useCreateFlyerType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createFlyerType,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["flyer-types"] });
            queryClient.invalidateQueries({ queryKey: ["campaigns", "flyer-types", "available"] });
            showSuccess("Flygbladet har skapats");
        },
        onError: (error: any) => {
            const message =
                error?.response?.data?.message || "Kunde inte skapa flygbladet";
            showError("Kunde inte skapa flygbladet", message);
        }
    });
};

export const useUpdateFlyerType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, request }: { id: string; request: FlyerTypeUpdateRequest }) =>
            updateFlyerType(id, request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["flyer-types"] });
            queryClient.invalidateQueries({ queryKey: ["campaigns", "flyer-types", "available"] });
            showSuccess("Flygbladet har uppdaterats");
        },
        onError: (error: any) => {
            const message =
                error?.response?.data?.message || "Kunde inte uppdatera flygbladet";
            showError("Kunde inte uppdatera flygbladet", message);
        }
    });
};

export const useDeleteFlyerType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteFlyerType,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["flyer-types"] });
            queryClient.invalidateQueries({ queryKey: ["campaigns", "flyer-types", "available"] });
            showSuccess("Flygbladet har tagits bort");
        },
        onError: (error: any) => {
            const message =
                error?.response?.data?.message || "Kunde inte ta bort flygbladet";
            showError("Kunde inte ta bort flygbladet", message);
        }
    });
};